import { PHONE_NUMBER } from "../../constants/ProductConstants";
import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function ProductDetails({ addToCart }) {
    const location = useLocation();
    const product = location.state?.product;

    const [selectedColorIndex, setSelectedColorIndex] = useState(0);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [qty, setQty] = useState(1);

    // 🔥 Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [product]);

    // 🔥 swipe states
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);
    const currentTranslate = useRef(0);
    const prevTranslate = useRef(0);

    if (!product) return <p>Product not found. Please navigate from a product listing page.</p>;

    const selectedColor = product.colors[selectedColorIndex];

    const images =
        selectedColor.carouselImages?.length > 0
            ? selectedColor.carouselImages
            : [selectedColor.mainImage || selectedColor.image];

    // 🔥 SLIDE HANDLERS
    const nextSlide = () =>
        setCurrentSlide((prev) => (prev + 1) % images.length);

    const prevSlide = () =>
        setCurrentSlide((prev) =>
            prev === 0 ? images.length - 1 : prev - 1
        );

    // 🔥 SWIPE LOGIC
    const handleStart = (x) => {
        setIsDragging(true);
        startX.current = x;
    };

    const handleMove = (x) => {
        if (!isDragging) return;
        currentTranslate.current = x - startX.current;
    };

    const handleEnd = () => {
        setIsDragging(false);

        if (currentTranslate.current < -50) nextSlide();
        if (currentTranslate.current > 50) prevSlide();

        currentTranslate.current = 0;
    };

    const handleBuyNow = () => {
        const phone = PHONE_NUMBER;
        const message = `Hi, I want to order:\n\n${product.name}\nColor: ${selectedColor.name}\nPrice: ₹${product.price}`;
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank");
    };

    return (
        <div style={styles.page}>
            <div
                style={{
                    ...styles.container,
                    paddingBottom: window.innerWidth <= 768 ? "190px" : styles.container.paddingBottom
                }}
            >
                {/* 🔥 CAROUSEL */}
                <div style={styles.images}>
                    <div
                        style={styles.carousel}
                        onMouseDown={(e) => handleStart(e.clientX)}
                        onMouseMove={(e) => handleMove(e.clientX)}
                        onMouseUp={handleEnd}
                        onMouseLeave={handleEnd}
                        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
                        onTouchEnd={handleEnd}
                    >
                        <div
                            style={{
                                ...styles.slider,
                                transform: `translateX(-${currentSlide * 100}%)`,
                            }}
                        >
                            {images.map((img, i) => (
                                <img key={i} src={img} style={styles.mainImg} />
                            ))}
                        </div>

                        {images.length > 1 && (
                            <>
                                <button style={styles.left} onClick={prevSlide}>
                                    ‹
                                </button>
                                <button style={styles.right} onClick={nextSlide}>
                                    ›
                                </button>
                            </>
                        )}
                    </div>

                    {/* DOTS */}
                    <div style={styles.dots}>
                        {images.map((_, i) => (
                            <span
                                key={i}
                                onClick={() => setCurrentSlide(i)}
                                style={{
                                    ...styles.dot,
                                    opacity: i === currentSlide ? 1 : 0.3,
                                    transform: i === currentSlide ? "scale(1.3)" : "scale(1)"
                                }}
                            />
                        ))}
                    </div>
                </div>

                {/* DETAILS */}
                <div style={styles.details}>
                    <h2 style={styles.title}>{product.name}</h2>

                    <div style={styles.price}>
                        <span style={styles.final}>₹{product.price}</span>
                        <span style={styles.original}>₹{product.originalPrice}</span>
                    </div>

                    <p style={styles.desc}>{product.description}</p>

                    {/* COLORS */}
                    <div style={styles.section}>
                        <p style={styles.sectionLabel}>Select Color</p>
                        <div style={styles.colors}>
                            {product.colors.map((c, i) => (
                                <button
                                    key={c.name}
                                    onClick={() => {
                                        setSelectedColorIndex(i);
                                        setCurrentSlide(0);
                                        setQty(1);
                                    }}
                                    style={{
                                        ...styles.colorBtn,
                                        background: c.hex,
                                        transform: i === selectedColorIndex ? "scale(1.15)" : "scale(1)",
                                        transition: "all 0.2s ease",
                                        border: "none",
                                        outline: i === selectedColorIndex
                                            ? "1px solid rgba(0,0,0,0.2)"
                                            : "none"
                                    }}
                                />
                            ))}

                        </div>
                    </div>
                    <span style={styles.colorName}>
                        {selectedColor.name}
                    </span>
                    {/* STOCK */}
                    <div style={styles.section}>
                        {selectedColor.stock <= 4 ? (
                            <p style={styles.stockWarning}>
                                Hurry! Only a few pieces left in stock.
                            </p>
                        ) : (
                            <p style={styles.stockText}>In stock</p>
                        )}
                    </div>

                    {/* QTY */}
                    <div style={styles.section}>
                        <p style={styles.infoTitle}>Quantity</p>
                        <div style={styles.qty}>
                            <button
                                style={styles.qtyBtn}
                                onClick={() => setQty(Math.max(1, qty - 1))}
                            >
                                −
                            </button>
                            <span style={styles.qtyCount}>{qty}</span>
                            <button
                                style={styles.qtyBtn}
                                onClick={() =>
                                    setQty(Math.min(selectedColor.stock, qty + 1))
                                }
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* FEATURES */}
                    {product.features && (
                        <div style={{ ...styles.section, ...styles.infoCard }}>
                            <p style={styles.infoTitle}>Features</p>
                            <ul style={styles.infoList}>
                                {product.features.map((feature, i) => (
                                    <li key={i} style={styles.infoListItem}>
                                        <span style={styles.infoBullet}>•</span>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* CARE TIPS */}
                    {product.careTips && (
                        <div style={{ ...styles.section, ...styles.infoCard }}>
                            <p style={styles.infoTitle}>Care Tips</p>
                            <ul style={styles.infoList}>
                                {product.careTips.map((tip, i) => (
                                    <li key={i} style={styles.infoListItem}>
                                        <span style={styles.infoBullet}>•</span>
                                        <span>{tip}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ACTIONS */}
                    {window.innerWidth > 768 && (
                        <div style={styles.actions}>
                            <button
                                style={styles.add}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 6px 14px rgba(0,0,0,0.12)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                                onMouseDown={(e) => {
                                    e.currentTarget.style.transform = "scale(0.97)";
                                }}
                                onMouseUp={(e) => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                }}
                                onClick={() =>
                                    addToCart({
                                        ...product,
                                        quantity: qty,
                                        selectedColor: selectedColor.name,
                                        selectedColorIndex,
                                        image: images[currentSlide]
                                    })
                                }
                            >
                                Add to Cart
                            </button>

                            <button
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-3px)";
                                    e.currentTarget.style.boxShadow = "0 10px 25px rgba(125, 18, 1, 0.35)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(125, 18, 1, 0.2)";
                                }}
                                onMouseDown={(e) => {
                                    e.currentTarget.style.transform = "scale(0.96)";
                                    e.currentTarget.style.boxShadow = "0 3px 8px rgba(125, 18, 1, 0.25)";
                                }}
                                onMouseUp={(e) => {
                                    e.currentTarget.style.transform = "translateY(-3px)";
                                    e.currentTarget.style.boxShadow = "0 10px 25px rgba(125, 18, 1, 0.35)";
                                }}
                                style={styles.buy}
                                onClick={handleBuyNow}
                            >
                                Buy Now
                            </button>

                        </div>
                    )}
                </div>

                {/* 📱 MOBILE STICKY BAR */}
                {window.innerWidth <= 768 &&
                    <div style={styles.mobileBar}>
                        <button
                            style={styles.mobileAdd}
                            onClick={() =>
                                addToCart({
                                    ...product,
                                    quantity: qty,
                                    selectedColor: selectedColor.name,
                                    selectedColorIndex,
                                    image: images[currentSlide]
                                })
                            }
                        >
                            Add to Cart
                        </button>

                        <button
                            style={styles.mobileBuy}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 8px 20px rgba(125,18,1,0.35)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(125,18,1,0.25)";
                            }}
                            onMouseDown={(e) => {
                                e.currentTarget.style.transform = "scale(0.96)";
                            }}
                            onMouseUp={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onClick={handleBuyNow}
                        >
                            Buy Now
                        </button>
                    </div>}
            </div>
        </div>
    );
}

/* 🎨 STYLES */
const styles = {
    page: {
        background: "#fff",
        minHeight: "100vh",
    },

    container: {
        display: "flex",
        gap: "40px",
        padding: "20px",
        maxWidth: "1100px",
        margin: "0px auto",
        flexWrap: "wrap",
        paddingBottom: "100px"
    },

    images: { flex: 1, minWidth: "280px" },

    carousel: {
        overflow: "hidden",
        borderRadius: "12px",
        position: "relative",
        cursor: "grab"
    },

    slider: {
        display: "flex",
        transition: "transform 0.4s ease"
    },

    mainImg: {
        width: "100%",
        flexShrink: 0,
        borderRadius: "14px",
        userSelect: "none",
        transition: "transform 0.4s ease, opacity 0.3s ease"
    },

    left: {
        position: "absolute",
        top: "50%",
        left: "10px",
        transform: "translateY(-50%)",
        background: "rgba(0,0,0,0.5)",
        color: "#fff",
        borderRadius: "50%",
        width: "35px",
        height: "35px",
        border: "none",
        cursor: "pointer"
    },

    right: {
        position: "absolute",
        top: "50%",
        right: "10px",
        transform: "translateY(-50%)",
        background: "rgba(0,0,0,0.5)",
        color: "#fff",
        borderRadius: "50%",
        width: "35px",
        height: "35px",
        border: "none",
        cursor: "pointer"
    },

    dots: {
        display: "flex",
        justifyContent: "center",
        gap: "6px",
        marginTop: "10px"
    },

    dot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: "#000",
        cursor: "pointer",
        transition: "0.2s"
    },

    details: { flex: 1, minWidth: "280px" },
    title: {
        fontFamily: "'Playfair Display', serif",
        fontSize: "20px",
        fontWeight: "500",
        lineHeight: "1.4",
        marginBottom: "6px",
        color: "#111",
    },

    price: {
        display: "flex",
        gap: "10px",
        alignItems: "center"
    },

    final: { fontSize: "22px", fontWeight: "bold" },

    original: {
        textDecoration: "line-through",
        color: "gray"
    },

    desc: {
        margin: "12px 0",
        color: "#555",
        fontSize: "14px",
        lineHeight: "1.6"
    },

    section: { margin: "20px 0" },

    infoCard: {
        background: "linear-gradient(145deg, #fffaf5 0%, #ffffff 100%)",
        border: "1px solid #f1dfc8",
        borderRadius: "14px",
        padding: "14px 16px",
        boxShadow: "0 6px 18px rgba(139, 107, 61, 0.08)"
    },

    infoTitle: {
        margin: "0 0 10px 0",
        fontWeight: 600,
        color: "#3d2e1f",
        fontSize: "15px",
        letterSpacing: "0.2px"
    },

    infoList: {
        margin: 0,
        padding: 0,
        listStyle: "none",
        display: "grid",
        gap: "7px"
    },

    infoListItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        color: "#4a5568",
        fontSize: "14px",
        lineHeight: 1.7
    },

    infoBullet: {
        color: "#b07b3d",
        fontWeight: 600,
        lineHeight: 1.4
    },

    colors: { display: "flex", gap: "10px" },
    colorName: {
        fontSize: "12px",
        color: "#000",
        opacity: 0.5,
        marginTop: "6px",
        display: "block"
    },
    sectionLabel: {
        fontSize: "13px",
        color: "#777",
        marginBottom: "6px"
    },

    colorBtn: {
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        cursor: "pointer"
    },

    qty: {
        display: "flex",
        gap: "12px",
        alignItems: "center"
    },

    qtyBtn: {
        width: "40px",
        height: "40px",
        borderRadius: "999px",
        border: "1px solid #d9c8b1",
        background: "linear-gradient(180deg, #fffdf9 0%, #f7eee2 100%)",
        color: "#5f4528",
        fontSize: "24px",
        lineHeight: 1,
        cursor: "pointer",
        boxShadow: "0 3px 8px rgba(108, 82, 46, 0.12)"
    },

    qtyCount: {
        minWidth: "64px",
        height: "40px",
        borderRadius: "999px",
        border: "1px solid #e7d6bf",
        background: "#fffaf2",
        color: "#3d2e1f",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        fontWeight: 600,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)"
    },

    actions: {
        display: "flex",
        gap: "10px",
        marginTop: "20px"
    },

    add: {
        flex: 1,
        padding: "12px",
        borderRadius: "10px",
        background: "#f1f1f1",
        border: "none",
        cursor: "pointer"
    },

    buy: {
        background: "linear-gradient(135deg, #7D1201, #A61B0F)",
        backgroundColor: "#7D1201",
        color: "#fff",
        border: "none",
        padding: "14px 28px",
        borderRadius: "999px",
        fontWeight: "600",
        fontSize: "15px",
        letterSpacing: "0.3px",
        cursor: "pointer",
        transition: "all 0.25s ease",
        boxShadow: "0 4px 12px rgba(125, 18, 1, 0.2)",
    },

    stockWarning: {
        color: "#d97706",
        fontWeight: "600"
    },

    stockText: {
        color: "#2e7d32",
        fontWeight: "500"
    },
    mobileBar: {
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        display: "flex",
        gap: "10px",
        padding: "12px 16px",
        background: "#fff",
        borderTop: "1px solid #eee",
        boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
        zIndex: 1000
    },

    mobileAdd: {
        flex: 1,
        padding: "12px",
        borderRadius: "10px",
        background: "#fff",
        border: "1.5px solid #7D1201",
        color: "#7D1201",
        fontWeight: "500",
        transition: "all 0.2s ease"
    },

    mobileBuy: {
        backgroundColor: "#7D1201",
        backgroundImage: "linear-gradient(135deg, #7D1201, #A61B0F)",
        color: "#fff",
        border: "none",
        padding: "12px",
        borderRadius: "10px",
        fontWeight: "600",
        fontSize: "15px",
        letterSpacing: "0.3px",
        cursor: "pointer",
        transition: "all 0.25s ease",
        boxShadow: "0 4px 12px rgba(125, 18, 1, 0.25)",
        flex: 1
    }


};